import { Rocket, Sparkles } from 'lucide-react';

/**
 * CosmicTypingCta — call-to-action card that launches the Cosmic Typing
 * game from the Book Detail modal's Resources tab.
 *
 * Visual: deep-space gradient with a starfield drawn via inline SVG
 * (stops at ~28KB compressed; no asset request needed). Sits inside the
 * existing 16px gap of the Resources tab so it stacks naturally above the
 * Learn With Ollie panel and Quiz CTA.
 *
 * Behaviour:
 *   - The whole card is the click target — large hit area is friendlier
 *     for kid-mode. A nested button would only confuse focus order, so
 *     the wrapper is a single <button>.
 *   - Disabled when there are too few words to make a meaningful run.
 *     The parent decides what "too few" means and just doesn't render
 *     us in that case; we still guard `disabled` defensively.
 */
export interface CosmicTypingCtaProps {
  wordCount: number;
  onLaunch: () => void;
  disabled?: boolean;
}

export function CosmicTypingCta({ wordCount, onLaunch, disabled }: CosmicTypingCtaProps) {
  return (
    <button
      type="button"
      onClick={onLaunch}
      disabled={disabled}
      aria-label={`Launch Cosmic Typing with ${wordCount} words from this book`}
      className="group relative mb-4 block w-full overflow-hidden rounded-2xl border border-indigo-900/40 bg-gradient-to-br from-[#0b0d2a] via-[#1a1b4b] to-[#3a1d6b] p-6 text-left shadow-or-card transition-transform hover:-translate-y-0.5 hover:shadow-or-pop focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-400/60 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
    >
      {/* Starfield — pure SVG so it's crisp at any size and themable.
          The two layers (small + medium stars) parallax-shift on hover
          via translate so the card feels alive without an animation
          loop chewing the main thread. */}
      <Starfield />

      {/* Drifting planet — adds depth without much weight. */}
      <span
        aria-hidden
        className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-gradient-to-br from-fuchsia-400/40 via-fuchsia-600/30 to-transparent blur-2xl transition-transform duration-500 group-hover:translate-x-2 group-hover:translate-y-2"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute -bottom-12 -left-10 h-40 w-40 rounded-full bg-gradient-to-tr from-cyan-400/25 via-blue-500/15 to-transparent blur-2xl"
      />

      <div className="relative flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-or-orange-400 to-fuchsia-500 text-white shadow-or-card transition-transform group-hover:rotate-[-8deg] group-hover:scale-110">
          <Rocket className="h-6 w-6" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300">
            <Sparkles className="h-3 w-3" aria-hidden />
            Cosmic Typing
          </div>
          <p className="mt-1 text-[13px] leading-relaxed text-indigo-100/85">
            Blast asteroids by typing the {wordCount} biggest words from this book. Build streaks,
            earn high scores, and warp through grades.
          </p>
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold text-white ring-1 ring-inset ring-white/20 transition-colors group-hover:bg-white/15">
            Launch game
            <span aria-hidden className="transition-transform group-hover:translate-x-0.5">
              →
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}

/**
 * Inline starfield — two scattered layers so different stars feel like
 * they're at different depths. Coordinates are hand-picked rather than
 * random so the layout is stable across renders (no flicker on re-mount).
 */
function Starfield() {
  return (
    <svg
      aria-hidden
      className="pointer-events-none absolute inset-0 h-full w-full opacity-70"
      viewBox="0 0 400 200"
      preserveAspectRatio="xMidYMid slice"
    >
      {/* Small stars */}
      {[
        [12, 18],
        [38, 80],
        [62, 36],
        [88, 150],
        [120, 22],
        [148, 92],
        [180, 60],
        [210, 175],
        [232, 28],
        [260, 110],
        [288, 14],
        [314, 70],
        [342, 162],
        [368, 44],
        [392, 130],
      ].map(([cx, cy], i) => (
        <circle key={`sm-${i}`} cx={cx} cy={cy} r={0.8} fill="white" opacity={0.65} />
      ))}
      {/* Medium stars with a soft glow */}
      {[
        [24, 50],
        [96, 110],
        [156, 32],
        [222, 124],
        [276, 70],
        [332, 100],
        [380, 24],
      ].map(([cx, cy], i) => (
        <g key={`md-${i}`}>
          <circle cx={cx} cy={cy} r={2.2} fill="white" opacity={0.18} />
          <circle cx={cx} cy={cy} r={1.2} fill="white" opacity={0.95} />
        </g>
      ))}
      {/* Tiny twinkle accents in cyan and orange — reuses brand hues */}
      <circle cx={70} cy={170} r={1.4} fill="#67e8f9" opacity={0.85} />
      <circle cx={250} cy={30} r={1.4} fill="#fb923c" opacity={0.85} />
      <circle cx={356} cy={138} r={1.2} fill="#f0abfc" opacity={0.9} />
    </svg>
  );
}
